import { css } from '@emotion/react'
import { ConsultaSeguimentoIndicatorBlock } from '@mchat-rf/consulta-seguimento/components/ConsultaSeguimentoIndicatorBlock'
import { ConsultaSeguimentoIndicatorHelper } from '@mchat-rf/consulta-seguimento/components/ConsultaSeguimentoIndicatorHelper'
import { ConsultaSeguimentoIndicatorResultado } from '@mchat-rf/consulta-seguimento/components/ConsultaSeguimentoIndicatorResultado'
import { ConsultaSeguimentoIndicatorStart } from '@mchat-rf/consulta-seguimento/components/ConsultaSeguimentoIndicatorStart'
import { ConsultaSeguimentoIndicatorStep } from '@mchat-rf/consulta-seguimento/components/ConsultaSeguimentoIndicatorStep'
import { ConsultaSeguimentoPerguntaHeader } from '@mchat-rf/consulta-seguimento/components/ConsultaSeguimentoPerguntaHeader'
import { ConsultaSeguimentoPerguntaHeaderTitle } from '@mchat-rf/consulta-seguimento/components/ConsultaSeguimentoPerguntaHeaderTitle'
import {
  ExemplosVerticalField,
  ExemplosVerticalFieldOptions,
} from '@mchat-rf/consulta-seguimento/components/ExemplosVerticalField'
import { isPerguntaStatusRespondendo } from '@mchat-rf/consulta-seguimento/consulta-seguimento-aplicacao/utils-consultaSeguimentoAplicacao'
import { PassouFalhouEnum } from '@mchat-rf/consulta-seguimento/enums-consultaSeguimento'
import { useConsultaSeguimentoAutoScroll } from '@mchat-rf/consulta-seguimento/hooks/useConsultaSeguimentoAutoScroll'
import { useConsultaSeguimentoSetResultado } from '@mchat-rf/consulta-seguimento/hooks/useConsultaSeguimentoSetResultado'
import { Heading, Text, VFlow } from 'bold-ui'
import { RadioOption, resolveName, SimNaoEnum } from 'components/form'
import { RadioGroupBoxField } from 'components/form/field/RadioGroupBoxField'
import { SimNaoSwitchButtonField } from 'components/form/field/SimNaoSwitchButtonField'
import { MChatPerguntaEnum } from 'graphql/types.generated'
import { RefObject } from 'react'
import { useField } from 'react-final-form'
import { isEmpty } from 'util/checks'
import { MetaPath } from 'util/metaPath'

import {
  ConsultaSeguimentoPergunta16ExemplosEnum,
  ConsultaSeguimentoPergunta16Model,
} from './model-consultaSeguimentoPergunta16'
import { PERGUNTA_16_SCROLL_CONFIG } from './scrollConfig-consultaSeguimentoPergunta16'
import {
  calculateResultadoPergunta16,
  conditionToCalculatePergunta16,
  hasPergunta16ExemplosFalhouAnySim,
  hasPergunta16ExemplosPassouAnySim,
  isPergunta16ExemplosAllFilled,
  isPergunta16ExemplosAllNao,
} from './utils-consultaSeguimentoPergunta16'

interface ConsultaSeguimentoPergunta16FieldProps {
  name: MetaPath<ConsultaSeguimentoPergunta16Model>
  containerRef: RefObject<HTMLDivElement>
  goNextButtonRef: RefObject<HTMLButtonElement>
}

export function ConsultaSeguimentoPergunta16Field(props: Readonly<ConsultaSeguimentoPergunta16FieldProps>) {
  const { name, containerRef, goNextButtonRef } = props

  const {
    input: { value },
  } = useField<ConsultaSeguimentoPergunta16Model>(resolveName(name), {
    subscription: { value: true },
  })

  useConsultaSeguimentoSetResultado<ConsultaSeguimentoPergunta16Model>({
    name,
    conditionToCalculate: conditionToCalculatePergunta16,
    calculateResultado: calculateResultadoPergunta16,
  })

  const { getId, triggerScrollFor } = useConsultaSeguimentoAutoScroll<ConsultaSeguimentoPergunta16Model>({
    name,
    containerRef,
    goNextButtonRef,
    scrollConfig: PERGUNTA_16_SCROLL_CONFIG,
  })

  const isPerguntaHeaderSim = value.PERGUNTA_16_HEADER === SimNaoEnum.SIM
  const isPerguntaExemplosAllNao = isPergunta16ExemplosAllNao(value?.PERGUNTA_16_EXEMPLOS)
  const hasPerguntaExemplosPassouAnySim = hasPergunta16ExemplosPassouAnySim(value?.PERGUNTA_16_EXEMPLOS)
  const hasPerguntaExemplosFalhouAnySim = hasPergunta16ExemplosFalhouAnySim(value?.PERGUNTA_16_EXEMPLOS)
  const hasSimOnlyInExemplosPassou = hasPerguntaExemplosPassouAnySim && !hasPerguntaExemplosFalhouAnySim
  const hasSimOnlyInExemplosFalhou = hasPerguntaExemplosFalhouAnySim && !hasPerguntaExemplosPassouAnySim
  const textoBasePassou = hasSimOnlyInExemplosPassou
    ? 'Sim somente para exemplos Passou:'
    : 'Os mais frequentes são exemplos de Passou:'

  const textResultadoFalhou = hasSimOnlyInExemplosFalhou
    ? 'Sim somente para exemplos Falhou:'
    : 'Os mais frequentes são exemplos de Falhou:'
  const textResultadoPassou = isPerguntaHeaderSim ? '' : textoBasePassou

  return (
    <VFlow vSpacing={0}>
      <VFlow>
        {isPerguntaStatusRespondendo(value.status) && (
          <ConsultaSeguimentoPerguntaHeader
            pergunta={MChatPerguntaEnum.PERGUNTA_16}
            title={
              <ConsultaSeguimentoPerguntaHeaderTitle
                prefix='Se você virar a cabeça para olhar para alguma coisa,'
                sufix=' olha ao redor para ver o que você está olhando?'
              />
            }
            status={value.status}
            field={
              <SimNaoSwitchButtonField
                name={name.PERGUNTA_16_HEADER}
                onChange={() => triggerScrollFor('PERGUNTA_16_HEADER')}
              />
            }
          />
        )}
        {!isPerguntaHeaderSim && !isEmpty(value.PERGUNTA_16_HEADER) && (
          <VFlow id={getId('PERGUNTA_16_EXEMPLOS')}>
            <VFlow vSpacing={0}>
              <ConsultaSeguimentoIndicatorStart />
              <ConsultaSeguimentoIndicatorHelper
                title='O que ele faz quando você se vira para olhar para alguma coisa?'
                subtitle='(Se o genitor não der um dos exemplos abaixo, perguntar cada um deles individualmente.)'
              />
            </VFlow>
            <VFlow vSpacing={0}>
              <ExemplosVerticalField<ConsultaSeguimentoPergunta16ExemplosEnum>
                name={name.PERGUNTA_16_EXEMPLOS}
                label='Responda todos os exemplos, conforme as respostas do genitor'
                options={PERGUNTA_16_EXEMPLOS_OPTIONS}
                title='Seu filho...'
                required
                onChange={() => triggerScrollFor('PERGUNTA_16_EXEMPLOS')}
              />
              {isPerguntaExemplosAllNao && <ConsultaSeguimentoIndicatorBlock />}
              {hasPerguntaExemplosPassouAnySim &&
                hasPerguntaExemplosFalhouAnySim &&
                isPergunta16ExemplosAllFilled(value.PERGUNTA_16_EXEMPLOS) && (
                  <VFlow vSpacing={0} id={getId('PERGUNTA_16_FREQUENCIA')}>
                    <ConsultaSeguimentoIndicatorStep
                      text={
                        <VFlow vSpacing={0}>
                          <Text color='inherit'>Sim tanto para exemplos Passou quanto Falhou:</Text>
                          <Heading level={3} color='inherit'>
                            Pergunta sobre a frequência
                          </Heading>
                        </VFlow>
                      }
                      style={styles.indicatorStep}
                    />
                    <RadioGroupBoxField
                      name={name.PERGUNTA_16_FREQUENCIA}
                      label='Quais os que ele faz com mais frequência?'
                      options={PERGUNTA_16_FREQUENCIA_OPTIONS}
                      required
                      onChange={() => triggerScrollFor('PERGUNTA_16_FREQUENCIA')}
                    />
                  </VFlow>
                )}
            </VFlow>
          </VFlow>
        )}
      </VFlow>
      {value.resultado && (
        <ConsultaSeguimentoIndicatorResultado
          text={{
            FALHOU: textResultadoFalhou,
            PASSOU: textResultadoPassou,
          }}
          resultado={value.resultado}
        />
      )}
    </VFlow>
  )
}

const PERGUNTA_16_EXEMPLOS_OPTIONS: ExemplosVerticalFieldOptions<ConsultaSeguimentoPergunta16ExemplosEnum> = {
  PASSOU: [
    {
      label: 'Olha para o que você está olhando?',
      value: ConsultaSeguimentoPergunta16ExemplosEnum.OLHA_PARA_O_QUE_VOCE_ESTA_OLHANDO,
    },
    {
      label: 'Aponta para o que você está olhando?',
      value: ConsultaSeguimentoPergunta16ExemplosEnum.APONTA_PARA_O_QUE_VOCE_ESTA_OLHANDO,
    },
    {
      label: 'Olha ao redor para ver o que você está olhando?',
      value: ConsultaSeguimentoPergunta16ExemplosEnum.OLHA_AO_REDOR_PARA_O_QUE_VOCE_ESTA_OLHANDO,
    },
  ],
  FALHOU: [
    {
      label: 'Não repara em você?',
      value: ConsultaSeguimentoPergunta16ExemplosEnum.NAO_REPARA_EM_VOCE,
    },
    {
      label: 'Olha para o seu rosto?',
      value: ConsultaSeguimentoPergunta16ExemplosEnum.OLHA_PARA_SEU_ROSTO,
    },
  ],
}

const PERGUNTA_16_FREQUENCIA_OPTIONS: RadioOption<PassouFalhouEnum>[] = [
  {
    label: 'Exemplos PASSOU',
    value: PassouFalhouEnum.PASSOU,
  },
  {
    label: 'Exemplos FALHOU',
    value: PassouFalhouEnum.FALHOU,
  },
]

const styles = {
  indicatorStep: css`
    width: auto;
  `,
}
